"use client";

/**
 * Client providers for App Router pages (WP migration).
 *
 * Mirrors the client-side concerns of the Pages Router `_app.js` so migrated
 * App Router routes behave identically: theme context, deferred GTM load, and
 * ad-param decoration of Zoom links. Faust/WordPressBlocks providers are
 * intentionally NOT here — App Router content is local MDX, not WP blocks.
 */
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { ThemeProvider } from "@/context/theme.context";
import type { ITheme } from "@/context/theme.context";
import { tagsEnabled, GTM_ID } from "@/utils/tags";

declare global {
    interface Window {
        __gtmLoaded?: boolean;
    }
}

function loadGTM() {
    if (window.__gtmLoaded) return;
    // Never load the live container on preview/local — it carries the Google Ads
    // conversion tags and Meta Pixel. See src/utils/tags.ts.
    if (!tagsEnabled()) return;
    window.__gtmLoaded = true;
    (function (w, d, s, l, i) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (w as any)[l] = (w as any)[l] || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (w as any)[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
        const f = d.getElementsByTagName(s)[0];
        const j = d.createElement(s) as HTMLScriptElement;
        const dl = l !== "dataLayer" ? "&l=" + l : "";
        j.async = true;
        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
        f.parentNode!.insertBefore(j, f);
    })(window, document, "script", "dataLayer", GTM_ID);
}

function decorateZoomLinks() {
    const params = new URLSearchParams(window.location.search);
    const adParams: Record<string, string> = {};
    for (const key of ["gclid", "fbclid", "li_fat_id"]) {
        const val = params.get(key);
        if (val) adParams[key] = val;
    }
    if (Object.keys(adParams).length === 0) return;

    document.querySelectorAll<HTMLAnchorElement>('a[href*="web.zoom.us"]').forEach((link) => {
        const url = new URL(link.href);
        Object.entries(adParams).forEach(([key, val]) => url.searchParams.set(key, val));
        link.href = url.toString();
    });
}

export default function SiteProviders({
    children,
    theme = "dark",
}: {
    children: React.ReactNode;
    theme?: ITheme["theme"];
}) {
    // Defer GTM off the initial main thread: first interaction or a long idle
    // fallback (matches _app.js — big mobile TBT/TTI win).
    useEffect(() => {
        const events = ["scroll", "mousemove", "touchstart", "keydown", "click"];
        const idle =
            window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 0));
        const fire = () => {
            idle(loadGTM);
            cleanup();
        };
        const cleanup = () => {
            events.forEach((e) => window.removeEventListener(e, fire));
            clearTimeout(timer);
        };
        events.forEach((e) =>
            window.addEventListener(e, fire, { once: true, passive: true }),
        );
        const timer = setTimeout(fire, 10000);
        return cleanup;
    }, []);

    const pathname = usePathname();
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const hasAdParams = ["gclid", "fbclid", "li_fat_id"].some((k) =>
            params.get(k),
        );
        if (!hasAdParams) return;
        decorateZoomLinks();
        let timer: ReturnType<typeof setTimeout>;
        const observer = new MutationObserver(() => {
            clearTimeout(timer);
            timer = setTimeout(decorateZoomLinks, 200);
        });
        observer.observe(document.body, { childList: true, subtree: true });
        return () => {
            observer.disconnect();
            clearTimeout(timer);
        };
    }, [pathname]);

    return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
